import { useCallback, useEffect, useMemo, useState, useTransition } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import { type FeedType, useFeedTypeStore } from 'store'

interface FeedNavProps {
  nav: FeedType[]
}

export function FeedNav({ nav }: FeedNavProps) {
  const { t } = useTranslation()

  const feedType = useFeedTypeStore(state => state.type)
  const setFeedType = useFeedTypeStore(state => state.changeFeedType)

  const [uiType, setUiType] = useState<FeedType>(feedType)

  const [isPending, startTransition] = useTransition()

  useEffect(() => {
    setUiType(feedType)
  }, [feedType])

  const handleClick = useCallback(
    (type: FeedType) => {
      setUiType(type)

      startTransition(() => {
        setFeedType(type)
      })
    },
    [setFeedType]
  )

  const items = useMemo(() => nav, [nav])

  return (
    <Wrapper aria-busy={isPending ? 'true' : 'false'}>
      <Swiper spaceBetween={5} slidesPerView="auto" style={{ height: '100%' }}>
        {items.map((navLink: FeedType, key: number) => (
          <SwiperSlide key={key} style={{ width: 'auto' }}>
        <Item
          $active={navLink === uiType}
          $isAll={(navLink.split('/').pop() ?? '').toLowerCase() === 'all'}
          onClick={() => handleClick(navLink)}
          aria-pressed={navLink === uiType}
        >
          {t(navLink.split('/').pop() ?? 'Missing')}
        </Item>
          </SwiperSlide>
        ))}
      </Swiper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  align-items: center;
  border-radius: 2rem;
  display: flex;
  height: 4rem;
  justify-content: space-around;
  padding: 0.5rem;
  background-color: #f5f5f6;
`

interface ItemProps {
  $active: boolean
  $isAll: boolean
}

const Item = styled.span<ItemProps>`
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  align-items: center;
  border-radius: 2rem;
  display: flex;
  font-family: Montserrat, sans-serif;
  font-size: 1.2rem;
  font-style: normal;
  font-weight: 600;
  height: 100%;
  justify-content: center;
  letter-spacing: 0.02em;
  line-height: 1.5rem;
  overflow: hidden;
  padding: ${({ $isAll }) => ($isAll ? '0 1.5rem' : '0 1.2rem')};
  text-decoration: none;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
  width: fit-content;
  color: #757576;

  ${({ $active }) => $active && `
    background: #262a33;
    color: #fff;
  `}
`